import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useDispatch } from "react-redux";
import { bulkDeleteMedia, bulkDeleteMediaStatus } from "../services/api";
import { showSnackBar } from "../utils/commonServices";
import {
  BULK_DELETE_POLL_INTERVAL,
  buildBulkDeletePayload,
  bulkDeleteResultFrom,
  bulkDeleteSelectionError,
  bulkDeleteSummaryMessage,
  bulkDeletionIdFrom,
  deletedIdsFrom,
  isQueuedBatchBroken,
  isQueuedResponse,
  mediaNamesById,
  normalizeMediaIds,
} from "../utils/bulkMediaDelete";

// Drives POST /media_files/bulk_destroy and, for a queued (202) batch, the
// bulk_destroy_status poll. Exposes one status for the UI:
//   'idle' | 'deleting' | 'queued' | 'done' | 'error'
export default (onDeleted) => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null); // { deleted, failed }
  const [queuedBatch, setQueuedBatch] = useState(null); // { id, total_count, status }
  const [jobError, setJobError] = useState(null);
  const [lastSubmittedIds, setLastSubmittedIds] = useState([]);
  // BMD-01/BMD-04: failed[].name is null for every refusal the caller may not
  // see. Snapshot the names at submit time - the list is refetched as soon as
  // the delete lands, so reading them back off the table afterwards is too late.
  const [submittedFileNames, setSubmittedFileNames] = useState({});

  const finish = (payload) => {
    const parsed = bulkDeleteResultFrom(payload);
    setResult(parsed);
    setStatus("done");
    showSnackBar(dispatch, bulkDeleteSummaryMessage(parsed));
    onDeleted && onDeleted(deletedIdsFrom(parsed), parsed);
  };

  const { mutate: bulkDeleteMutate } = useMutation(bulkDeleteMedia, {
    onSuccess: (response) => {
      const responseData = response?.data?.data;
      // Do not branch on data.http_code — it is hardcoded to 200 on both paths.
      if (isQueuedResponse(response)) {
        setQueuedBatch({
          id: bulkDeletionIdFrom(response),
          total_count: responseData?.total_count,
          status: responseData?.status,
        });
        setStatus("queued");
        return;
      }
      finish(responseData);
    },
    onError: (error) => {
      const errorObject = error?.response?.data;
      setJobError(errorObject?.message || "Something went wrong. Please try again.");
      setStatus("error");
      showSnackBar(dispatch, errorObject);
    },
  });

  // Poll only while a queued batch is in flight.
  const { data: statusData, error: statusError } = useQuery(
    [`bulkDeleteStatus${queuedBatch?.id}`, queuedBatch?.id],
    () => bulkDeleteMediaStatus({ bulk_deletion_id: queuedBatch?.id }),
    {
      enabled: status === "queued" && !!queuedBatch?.id,
      refetchInterval: BULK_DELETE_POLL_INTERVAL,
      retry: false,
    }
  );

  useEffect(() => {
    if (status !== "queued") return;
    const data = statusData?.data?.data;
    if (!data) return;

    setQueuedBatch((prev) => ({ ...prev, ...data }));

    // Stop on finished: true — it covers both 'completed' and 'failed'.
    if (data?.finished !== true) return;

    if (isQueuedBatchBroken(data)) {
      setJobError(
        data?.error_message ||
          "The delete job didn't finish. Please select the files and try again."
      );
      setStatus("error");
      showSnackBar(dispatch, {
        http_code: 422,
        message: "The delete job didn't finish. Please try again.",
      });
      return;
    }
    finish(data);
  }, [statusData, status]);

  useEffect(() => {
    if (status !== "queued" || !statusError) return;
    const errorObject = statusError?.response?.data;
    setJobError(errorObject?.message || "Couldn't read the delete progress.");
    setStatus("error");
    showSnackBar(dispatch, errorObject);
  }, [statusError]);

  const startBulkDelete = (ids, files) => {
    const normalizedIds = normalizeMediaIds(ids);
    const selectionError = bulkDeleteSelectionError(normalizedIds);
    if (selectionError) {
      setJobError(selectionError);
      setStatus("error");
      showSnackBar(dispatch, { http_code: 422, message: selectionError });
      return;
    }
    setLastSubmittedIds(normalizedIds);
    // A retry re-sends the same ids, so keep the names it already holds when the
    // caller has nothing fresh to give.
    if (files !== undefined) setSubmittedFileNames(mediaNamesById(files));
    setResult(null);
    setQueuedBatch(null);
    setJobError(null);
    setStatus("deleting");
    bulkDeleteMutate(buildBulkDeletePayload(normalizedIds));
  };

  // A failed job is never retried server-side, so a re-submit is a fresh call.
  const retryBulkDelete = () => startBulkDelete(lastSubmittedIds);

  const resetBulkDelete = () => {
    setStatus("idle");
    setResult(null);
    setQueuedBatch(null);
    setJobError(null);
  };

  return {
    startBulkDelete,
    retryBulkDelete,
    resetBulkDelete,
    bulkDeleteStatus: status,
    bulkDeleteResult: result,
    bulkDeleteQueuedBatch: queuedBatch,
    bulkDeleteError: jobError,
    bulkDeleteFileNames: submittedFileNames,
    isBulkDeleting: status === "deleting" || status === "queued",
  };
};
